import { type Address, parseEther } from 'viem';
import { goerli, mainnet, optimism, optimismGoerli } from 'viem/chains';
import type { PublicClient } from 'wagmi';
import { assertAddress } from '@soundxyz/utils/address';

export function chainFromId(chainId: number) {
  switch (chainId) {
    case 10:
      return optimism;
    case 1:
      return mainnet;
    case 420:
      return optimismGoerli;
    case 5:
      return goerli;
    default: {
      throw Error(`Unsupported chain id ${chainId}`);
    }
  }
}

const ESTIMATED_UPLOAD_GAS = 1_000_000n;

export async function hasEnoughForGasEstimate({
  gasEstimate,
  userAddress,
  publicClient,
  extraValue,
}: {
  gasEstimate: bigint;
  userAddress: Address;
  publicClient: PublicClient;
  extraValue: bigint | null;
}) {
  const value = extraValue ?? 0n;

  const [balance, baseFeePerGas] = await Promise.all([
    publicClient.getBalance({
      address: userAddress,
      blockTag: 'latest',
    }),
    publicClient
      .getBlock({
        blockTag: 'pending',
      })
      .then(v => v.baseFeePerGas ?? 0n),
  ]);

  const totalGasEstimate = gasEstimate * (baseFeePerGas + ESTIMATED_UPLOAD_GAS);

  return balance >= totalGasEstimate + value;
}

export async function hasEnoughFundsForL2Upload({
  userAddress,
  publicClient,
}: {
  userAddress: Address;
  publicClient: PublicClient;
}) {
  const balance = await publicClient.getBalance({
    address: userAddress,
    blockTag: 'latest',
  });

  // safe constant for l2 uploads, to show a preemptive insufficient funds modal
  // if the tx cost is higher, this is also handled at wallet layer and transactions.tsx, so don't worry
  return balance > parseEther('0.0005');
}

export async function isDeployedContractAddress({
  contractAddress,
  provider,
}: {
  contractAddress: string;
  provider: PublicClient;
}) {
  assertAddress(contractAddress);

  const byteCode = await provider.getBytecode({
    address: contractAddress,
  });

  return byteCode != null && byteCode !== '0x';
}
